import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { AttachmentCarouselModal } from '../../../components/modals/AttachmentCarouselModal';
import { RichHtmlContent } from '../../../components/RichHtmlContent';
import { toRenderableRichHtml } from '../../../components/richHtmlContentShared';
import { inlineImageOpenFromPress } from '../../../utils/richContent/inlineImageRef';

import type { CarouselMedia } from '../../../components/modals/AttachmentCarouselModal';
import type { InlineImageOpenRequest } from '../../../utils/richContent/inlineImageRef';

interface SummaryRichTextValueProps {
  html: string;
  requestId?: string;
  testID?: string;
  /** Viewer header. The summary row passes its field label. */
  sectionLabel?: string;
}

/**
 * Read-only rich text on the summary step. A picture opens the same
 * full-file viewer as a rich text field.
 */
export const SummaryRichTextValue: React.FC<SummaryRichTextValueProps> = ({
  html,
  requestId,
  testID,
  sectionLabel = 'Summary',
}) => {
  const [inlinePreview, setInlinePreview] =
    useState<InlineImageOpenRequest | null>(null);
  const openInlinePreview = useCallback((request: InlineImageOpenRequest) => {
    setInlinePreview(request);
  }, []);
  useEffect(() => {
    setInlinePreview(null);
  }, [html, requestId]);
  const inlinePreviewItems = useMemo((): CarouselMedia[] => {
    if (!inlinePreview) {
      return [];
    }
    return [
      {
        id: String(inlinePreview.attachmentId),
        name: inlinePreview.name,
        mimeType: 'image/*',
        previewable: true,
      },
    ];
  }, [inlinePreview]);

  if (!html.trim()) {
    return null;
  }

  return (
    <>
      <RichHtmlContent
        value={html}
        requestId={requestId}
        testID={testID}
        onImagePress={(src, ordinal) => {
          const request = inlineImageOpenFromPress(
            toRenderableRichHtml(html),
            src,
            ordinal,
          );
          if (request) {
            openInlinePreview(request);
          }
        }}
      />
      {inlinePreview ? (
        <AttachmentCarouselModal
          visible
          onClose={() => setInlinePreview(null)}
          items={inlinePreviewItems}
          initialIndex={0}
          sectionLabel={sectionLabel}
        />
      ) : null}
    </>
  );
};
